import React, { useState, useEffect, useRef } from "react";
import {
    Row,
    Col,
    Button,
    message,
    Popconfirm,
    ProTableOt,
    useProTableOt,
    setParamsFilter,
} from "../../ui/ot";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../ui/card'
import { useCRUDController } from "../../ui/classify_structure/CRUDController";
import useWindowSize from '../../online_public_catalog_query_construction/controllers/window_size';
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Clock, RotateCw } from "lucide-react"
import { Badge } from "../../ui/badge"

const BorrowedBooks = ({
    keyPrefix = "library.personal_study",
    borrowernumber,
    enableBorrowTimeSorting = false,
    showCard ={ 
        xxl: 4,
        xl: 3,
        lg: 3,
        md: 2,
        sm: 2,
        xs: 1,
     }
}) => {
    const { t } = useTranslation('translation', { keyPrefix })
    const actionRef = useRef();
    const borrowSortDirectionRef = useRef("asc");
    const [borrowSortDirection, setBorrowSortDirection] = useState("asc");
    const columns = [

        {
            title: t(`book_title`),
            dataIndex: 'bib_title',
            key: 'bib_title',
        },
        {
            title: t(`book_title`),
            dataIndex: 'bib_subtitle',
            key: 'bib_subtitle',
        },
        {
            title: t(`overdue_status`),
            dataIndex: 'issue_days_due_status',
            key: 'issue_days_due_status',
        },
        {
            title: t(`author_name`),
            dataIndex: 'bib_author',
            key: 'bib_author',
        },
    ];
  
    const [loading,setLoading] = useState(false)
    // 螢幕大小斷點判斷
    function getBreakpoint(width) {
        const breakpoints = {
            xxl: 1600,
            xl: 1200,
            lg: 992,
            md: 768,
            sm: 576,
            xs: 0,
        };
        if (width >= breakpoints.xxl) return 'xxl';
        if (width >= breakpoints.xl) return 'xl';
        if (width >= breakpoints.lg) return 'lg';
        if (width >= breakpoints.md) return 'md';
        if (width >= breakpoints.sm) return 'sm';
        return 'xs';
    }
    const onRenewFinish = ({ axiosMode, status, values }) => {
        if (status === "success") {
            if (axiosMode === "post") {
                message.success(t('renew_success'))
                actionRef.current.reloadAndRest();
            }
            else if (axiosMode === "patch") {
                message.success(t('update_success'))
                actionRef.current.reload();
            }
            else if (axiosMode === "delete") {
                message.success(t('delete_success'))
                actionRef.current.reloadAndRest();
            }
        }
        else if (status === "error") {
            if (axiosMode === "post") {
                if (values === "Renewal not authorized (too_many)") {
                    message.error(t('renew_failure_limit'))
                }
                else if (values === "Renewal not authorized (too_soon)") {
                    message.error(t('renew_failure_not_open'))
                }
                else if (values === "Renewal not authorized (on_reserve)") {
                    message.error(t('renew_failure_reserved'))
                }
                else message.error(t('renew_failure'))
            }
            else if (axiosMode === "patch") {
                message.error(t('update_failure'))
            }
            else if (axiosMode === "delete") {
                message.error(t('delete_failure'))
            }
        }
        setLoading(false)
    }
    //取得螢幕大小
    const { width, height } = useWindowSize();
    const LibraryCheckoutController = useCRUDController({
        apiRoute: !!borrowernumber ? "/library/koha/checkout" : "/library/koha/checkout/self",
    })
    const KohaCheckoutRenewController = useCRUDController({
        apiRoute: "/koha/checkout/renew",
        onFinish: onRenewFinish,
        onBefore: ({ axiosMode, status })=>{
            if(axiosMode==="post"){
                setLoading(true)
            }
        }
    })
    const {
        check,
        setCheck,
        checkAll,
        setOrders,
        custom_filter_key,
    } = useProTableOt({
        columns,
    });
    useEffect(() => {
        checkAll();
    }, []);

    const toggleBorrowSortDirection = () => {
        const nextDirection = borrowSortDirectionRef.current === "asc" ? "desc" : "asc";
        borrowSortDirectionRef.current = nextDirection;
        setBorrowSortDirection(nextDirection);
        actionRef.current?.onTableSearch();
    };

    return (<Row gutter={[16, 0]}>
        <Col span={24}>
            <ProTableOt
                check={check}
                setCheck={setCheck}
                reloadButton={false}
                manageColumnsButton={false}
                showSearch={true}
                readonly={true}
                actionRef={actionRef}
                showMode="card"
                headerTitle={
                    <>

                    </>
                }
                columns={columns}
                rowKey={'item_itemnumber'}
                toolBarRender={() => enableBorrowTimeSorting ? [
                    <Button
                        key="borrow-time-sort"
                        type="button"
                        variant="outline"
                        onClick={toggleBorrowSortDirection}
                    >
                        {borrowSortDirection === "asc"
                            ? <ArrowUp className="mr-2 h-4 w-4" />
                            : <ArrowDown className="mr-2 h-4 w-4" />}
                        {t(borrowSortDirection === "asc" ? 'borrow_time_oldest_first' : 'borrow_time_newest_first')}
                    </Button>
                ] : []}
                manage_columns={false}
                request={async (params = {}, sort, filter) => {
                    let newParams = {};
                    let order = enableBorrowTimeSorting
                        ? setOrders({
                            issue_issuedate: borrowSortDirectionRef.current === "asc" ? "ascend" : "descend",
                        })
                        : setOrders(sort);
                    newParams = {
                        ...params,
                        ...newParams,
                        borrowernumber,
                        order,
                        size: params?.pageSize,
                        cur_page: params.current,
                        custom_filter_value: params.keyword ?? "",
                        custom_filter_key: custom_filter_key,
                    };
                    newParams = setParamsFilter({
                        ...newParams,
                    });
                    return LibraryCheckoutController.getData(newParams)
                }}
                cardModeSetting={{
                    columnsCount: showCard[getBreakpoint(width)],
                    singleRowHeight: "320px",
                }}
                cardRender={(item, { PatchButton, DeleteButton }) => {
                    const isNearDue = item?.issue_days_due > 0
                    const isOverdue = item?.issue_days_over > 0
                    return (
                        <Card key={item?.id} className="overflow-hidden border-[#e8e0d8] bg-white h-full" >
                            <CardHeader className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="space-y-1">
                                        <CardTitle className="line-clamp-1">{item?.bib_title}</CardTitle>
                                        <CardDescription>{item?.bib_author}</CardDescription>
                                    </div>
                                    {isOverdue ? (
                                        <Badge className="text-nowrap whitespace-nowrap" variant="destructive">{item?.issue_days_due_status}</Badge>
                                    ) : isNearDue ? (
                                        <Badge variant="outline" className="text-nowrap whitespace-nowrap bg-amber-100 text-amber-800 hover:bg-amber-100">
                                            {item?.issue_days_due_status}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-nowrap whitespace-nowrap border-[#e8a05c] text-[#e8a05c]">
                                            {item?.issue_days_due_status}
                                        </Badge>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="p-4 pt-0">
                                <div className="flex gap-4">
                                    <div className="shrink-0">
                                        <img
                                            src={"https://media.taaze.tw/showLargeImageByIsbn.ashx?height=100%&isbn=" +
                                                item?.bibitem_isbn}
                                            alt={item?.title}
                                            className="h-[120px] w-[80px] rounded-md object-cover"
                                        />
                                    </div>
                                    <div className="flex flex-col justify-between">
                                        <div className="space-y-1">
                                            <div className="flex items-center text-sm">
                                                <Clock className="mr-1 h-4 w-4 text-muted-foreground" />
                                                <span className={isOverdue ? "text-destructive font-medium" : ""}>
                                                    {isOverdue ? `${t('overdue')} ${item?.issue_days_over || "-"} ${t('day')}` : `${t('still_days_left')} ${item?.issue_days_due || "-"} ${t('due_in_days')}`}
                                                </span>
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {t('item_barcode')}: {item?.item_barcode} 
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {t('borrow_time')}: {item?.issue_issuedate}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {t('note')}: {item?.issue_note} 
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {t('due_date')}: { item?.issue_date_due }
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {t('renewal_count')}: {item?.issue_renewals_count}/{item?.issue_allow_renewals_count}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter className="p-4 pt-0">
                                <Popconfirm
                                    title={t('confirm_renewal')}
                                    description={t('renewal_prompt')}
                                    onConfirm={() => {
                                        KohaCheckoutRenewController.postData([
                                            {
                                                "issue_id": item?.issue_id,  //必填*
                                            }
                                        ])
                                    }}

                                >
                                    <Button
                                        loading={loading}
                                        variant="outline"
                                        className="w-full border-[#e8e0d8] text-[#e8a05c] hover:bg-[#f0e6da] hover:text-[#e8a05c]"
                                        disabled={!item?.issue_allow_renewal}
                                    >
                                        <RotateCw className="mr-2 h-4 w-4" />
                                        {t('renew')}
                                    </Button>
                                </Popconfirm>
                            </CardFooter>
                        </Card>
                    )
                }}
                rowSelection={false}
                propsPatch={false}
                propsDelete={false}
                propsPost={false}
                propsWidth={2400}
                scroll={{
                    x: "100%",
                }}
            />
        </Col>
    </Row>)
}

export default BorrowedBooks
